// Flow-step UI primitives.
//
// Every flow is built as a vertical list of <StepCard> blocks. Each step
// has a numbered header, a description, an optional "needs input from a
// previous step" guard, and a body with form fields + a Run button.

import { useState, type ReactNode } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { executeRequest } from '@/api/client';
import { CodeTabs } from '@/components/CodeTabs';
import { PayloadViewer } from '@/components/PayloadViewer';
import { ResponseViewer } from '@/components/ResponseViewer';
import { SequenceDiagram } from '@/components/SequenceDiagram';
import type { ApiResponseSnapshot, RequestSpec } from '@/types';
import { useFlow } from './_state';
import type { FlowContext, FlowContextKey } from './types';

// =============================================================================
// StepCard — one step in a flow
// =============================================================================

interface StepCardProps {
  stepId: string;
  index: number;
  total: number;
  title: string;
  /** One-line subtitle ("POST /media/projects" etc.). */
  subtitle?: string;
  description?: string;
  /** Keys that must be present on `flow.context` before this step is runnable. */
  requires?: FlowContextKey[];
  children?: ReactNode;
}

export function StepCard({
  stepId,
  index,
  total,
  title,
  subtitle,
  description,
  requires,
  children,
}: StepCardProps) {
  const flow = useFlow();
  const result = flow.results[stepId];
  const isCompleted = !!result?.ranAt && (result.response?.ok ?? true);
  const isActive = flow.activeStepId === stepId;
  const showBody = isActive || isCompleted;

  // Determine missing dependencies (if any).
  const missing = (requires || []).filter((k) => !flow.context[k]);

  return (
    <section
      className="lp-shadow-card"
      style={{
        background: '#ffffff',
        border: isActive ? '1px solid #cbc4ff' : '1px solid #e5e7eb',
        borderRadius: 22,
        boxShadow: isActive ? '0 18px 42px rgba(98,86,255,0.10)' : undefined,
        scrollMarginTop: 24,
      }}
      id={`step-${stepId}`}
      aria-current={isActive ? 'step' : undefined}
    >
      <header
        className="lp-flex lp-items-start lp-gap-3 lp-px-6 lp-py-5 lp-cursor-pointer lp-select-none"
        onClick={() => flow.setActiveStepId(stepId)}
      >
        <span
          aria-hidden
          className="lp-shrink-0 lp-flex lp-items-center lp-justify-center lp-text-xs lp-font-bold"
          style={{
            width: 30,
            height: 30,
            borderRadius: 9999,
            background: isCompleted
              ? '#10b981'
              : isActive
                ? 'linear-gradient(135deg, #6256ff, #7c3aed)'
                : '#f1f5f9',
            color: isCompleted || isActive ? '#ffffff' : '#94a3b8',
            boxShadow: isActive ? '0 6px 14px rgba(98,86,255,0.28)' : 'none',
          }}
        >
          {isCompleted ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            index + 1
          )}
        </span>
        <div className="lp-flex-1 lp-min-w-0">
          <div className="lp-flex lp-items-baseline lp-gap-2 lp-flex-wrap">
            <h3 className="lp-text-base lp-font-bold lp-tracking-tight" style={{ color: '#0f172a' }}>
              {title}
            </h3>
            {subtitle && (
              <code
                className="lp-font-mono"
                style={{
                  fontSize: 11.5,
                  background: '#f1f0ff',
                  color: '#4030d4',
                  padding: '1px 6px',
                  borderRadius: 4,
                }}
              >
                {subtitle}
              </code>
            )}
          </div>
          {description && (
            <p className="lp-text-[13px] lp-mt-1" style={{ color: '#64748b' }}>
              {description}
            </p>
          )}
        </div>
        <span
          className="lp-text-[10px] lp-font-bold lp-uppercase lp-tracking-wider lp-shrink-0"
          style={{ color: isCompleted ? '#047857' : isActive ? '#6256ff' : '#94a3b8' }}
        >
          {isCompleted ? 'Done' : isActive ? 'Active' : `Step ${index + 1} of ${total}`}
        </span>
      </header>
      {showBody && (
        <div
          className="lp-px-6 lp-pb-6 lp-pt-1"
          style={{ borderTop: '1px solid #f1f5f9' }}
        >
          {missing.length > 0 ? (
            <MissingDeps requires={missing} />
          ) : (
            children
          )}
        </div>
      )}
    </section>
  );
}

function MissingDeps({ requires }: { requires: FlowContextKey[] }) {
  return (
    <div
      className="lp-px-4 lp-py-3 lp-flex lp-items-start lp-gap-3 lp-text-sm"
      style={{
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 12,
        color: '#78350f',
      }}
    >
      <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" className="lp-shrink-0 lp-mt-0.5">
        <path
          fillRule="evenodd"
          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0V7a1 1 0 0 1 1-1zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
          clipRule="evenodd"
        />
      </svg>
      <span>
        <span className="lp-font-semibold">This step needs:</span>{' '}
        {requires.map((k) => (
          <code
            key={k}
            className="lp-font-mono"
            style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: 4, marginRight: 4 }}
          >
            {k}
          </code>
        ))}
        — finish the previous step(s) first.
      </span>
    </div>
  );
}

// =============================================================================
// StepBody — payload + code + run + response, identical to PlaygroundPanel
// but wired to flow state.
// =============================================================================

interface StepBodyProps {
  stepId: string;
  spec: RequestSpec | null;
  /** Form / inputs for this step — the spec is computed from these. */
  children: ReactNode;
  /**
   * Called after a successful run with the parsed response. The handler
   * should compute and return the `produced` ids dictionary for the flow
   * context (or null/undefined to produce nothing).
   */
  onRunSuccess?: (response: ApiResponseSnapshot) => Partial<FlowContext> | undefined | void;
  /** Custom CTA copy (default: "Run step N"). */
  runLabel?: string;
  /** Disable the run button irrespective of state (e.g. missing required fields). */
  runDisabledReason?: string;
  /** Hide the response viewer block (used when a custom one is rendered above). */
  hideResponse?: boolean;
}

export function StepBody({
  stepId,
  spec,
  children,
  onRunSuccess,
  runLabel,
  runDisabledReason,
  hideResponse,
}: StepBodyProps) {
  const { env, getValidAccessToken } = useAuth();
  const flow = useFlow();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const result = flow.results[stepId];
  const response = result?.response ?? null;

  const run = async () => {
    if (!spec) return;
    setError(null);
    setRunning(true);
    try {
      const token = getValidAccessToken();
      if (!token) throw new Error('Your session expired — sign in again.');
      const r = await executeRequest(env, spec, token);
      const produced = r.ok && onRunSuccess ? onRunSuccess(r) || {} : undefined;
      flow.recordStepResult(stepId, { spec, response: r, produced });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="lp-grid lp-grid-cols-1 xl:lp-grid-cols-[minmax(0,1.35fr)_minmax(360px,440px)] lp-gap-6 lp-mt-3">
      <div className="lp-space-y-4">
        {children}
        <button
          onClick={run}
          disabled={!spec || running || !!runDisabledReason}
          className="lp-btn-primary lp-w-full lp-py-2.5 lp-text-sm lp-font-bold"
          style={{ borderRadius: 12 }}
          title={runDisabledReason}
        >
          {running ? 'Running…' : runLabel || `Run step`}
        </button>
        {runDisabledReason && (
          <p className="lp-text-[11.5px]" style={{ color: '#94a3b8' }}>
            {runDisabledReason}
          </p>
        )}
      </div>
      <div className="lp-space-y-4 lp-self-start">
        {spec && <PayloadViewer spec={spec} />}
        {spec && <CodeTabs env={env} spec={spec} hasAuth />}
        {!hideResponse && (
          <ResponseViewer response={response} loading={running} error={error} />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// FlowHeader — title + description + restart button
// =============================================================================

interface FlowHeaderProps {
  eyebrow?: string;
  title: string;
  description: string;
  steps: { id: string; label: string; hint?: string }[];
}

export function FlowHeader({ eyebrow, title, description, steps }: FlowHeaderProps) {
  const flow = useFlow();
  return (
    <header className="lp-mb-5">
      <div className="lp-flex lp-items-start lp-justify-between lp-gap-4 lp-flex-wrap">
        <div className="lp-min-w-0">
          {eyebrow && (
            <div
              className="lp-text-[10.5px] lp-font-bold lp-uppercase lp-mb-1.5"
              style={{ letterSpacing: '0.14em', color: '#6256ff' }}
            >
              {eyebrow}
            </div>
          )}
          <h1 className="lp-text-2xl lp-font-bold lp-tracking-tight" style={{ color: '#0f172a' }}>
            {title}
          </h1>
          <p className="lp-text-sm lp-mt-1.5 lp-max-w-2xl" style={{ color: '#475569' }}>
            {description}
          </p>
        </div>
        <button
          onClick={flow.reset}
          className="lp-text-[11.5px] lp-font-semibold lp-px-3 lp-py-1.5 lp-rounded-lg lp-shrink-0"
          style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            color: '#475569',
          }}
        >
          ↻ Restart flow
        </button>
      </div>
      <div className="lp-mt-4 lp-overflow-x-auto">
        <FlowSequence steps={steps} />
      </div>
    </header>
  );
}

// =============================================================================
// FlowSequence — the sequence diagram strip wired to flow state
// =============================================================================

function FlowSequence({ steps }: { steps: { id: string; label: string; hint?: string }[] }) {
  const flow = useFlow();
  const overrides: Record<string, 'done' | 'active' | 'pending'> = {};
  for (const s of steps) {
    const r = flow.results[s.id];
    if (r?.ranAt && (r.response?.ok ?? true)) overrides[s.id] = 'done';
  }
  return (
    <SequenceDiagram
      steps={steps.map((s) => ({
        ...s,
        onClick: () => {
          flow.setActiveStepId(s.id);
          requestAnimationFrame(() => {
            const el = document.getElementById(`step-${s.id}`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        },
      }))}
      activeId={flow.activeStepId}
      statusOverrides={overrides}
    />
  );
}
